import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PaginationInfo } from "../../constants/dataTypes";

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ pagination, onPageChange }) => {
  const { page, limit, total, pages } = pagination;

  const getVisiblePages = () => {
    const visiblePages = [];
    const maxVisiblePages = 5;
    
    if (pages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= pages; i++) {
        visiblePages.push(i);
      }
    } else {
      // Always show first page
      visiblePages.push(1);
      
      // Calculate start and end of visible pages
      let start = Math.max(2, page - 1);
      let end = Math.min(pages - 1, page + 1);
      
      // Adjust if we're near the start
      if (page <= 3) {
        end = 4;
      }
      
      // Adjust if we're near the end
      if (page >= pages - 2) {
        start = pages - 3;
      }
      
      // Add ellipsis after first page if needed
      if (start > 2) {
        visiblePages.push('...');
      }
      
      // Add middle pages
      for (let i = start; i <= end; i++) {
        visiblePages.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (end < pages - 1) {
        visiblePages.push('...');
      }
      
      // Always show last page
      visiblePages.push(pages);
    }
    
    return visiblePages;
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-6 border-t border-gray-200 dark:border-gray-700">
      <div className="text-sm text-gray-500 dark:text-gray-400">
        Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} results
      </div>
      
      <div className="flex items-center gap-2">
        {/* Previous Button */}
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 text-sm"
        >
          <ChevronLeft size={16} />
          Previous
        </button>

        {/* Page Numbers */}
        <div className="flex gap-1">
          {getVisiblePages().map((pageNum, index) => (
            <button
              key={index}
              onClick={() => typeof pageNum === 'number' && onPageChange(pageNum)}
              disabled={pageNum === '...'}
              className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors duration-200 min-w-[40px] ${
                pageNum === '...'
                  ? 'border-transparent text-gray-500 dark:text-gray-400 cursor-default'
                  : page === pageNum
                  ? 'border-blue-500 bg-blue-500 text-white'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              {pageNum}
            </button>
          ))}
        </div>

        {/* Next Button */}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === pages}
          className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 text-sm"
        >
          Next
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;